import type { CourseConfig } from './types';

// Base shared pages
import CourseLandingPage from '../pages/course/CourseLandingPage';
import DemoActivityPage from './DEMO/steps/ActivityPage';
import { PreferencesPage as BasePreferences } from '../pages/preferences/PreferencesPage';
import { ChatPage as BaseChat } from '../pages/chat/ChatPage';
// RMIT1 custom pages
import RMIT1Landing from './RMIT1/steps/Landing';
// ADHDWRITE1 custom pages
import ADHDWRITE1Landing from './ADHDWRITE1/steps/LandingWithQuestionnaire';
import ADHDWRITE1ThankYou from './ADHDWRITE1/steps/ThankYouPage';
// ND101 custom pages
import ND101Landing from './ND101/steps/Landing';
// ND101-ACON1 custom pages
import ND101ACON1Landing from './ND101-ACON1/steps/Landing';
// ND101-ACON1A custom pages
import ND101ACON1AActivityPage from './ND101-ACON1A/steps/ActivityPage';
import ND101ACON1ALanding from './ND101-ACON1A/steps/Landing';

// NOTE: Custom steps can be added per-course using lazy imports like below.
// Example placeholders (not used yet):
// const RMIT1_Intro = React.lazy(() => import('../courses/RMIT1/steps/Intro'));
// const RMIT1_Safety = React.lazy(() => import('../courses/RMIT1/steps/Safety'));
// const RMIT1_Activity = React.lazy(() => import('../courses/RMIT1/steps/Activity'));

export const courseRegistry: Record<string, CourseConfig> = {
  DEMO: {
    code: 'DEMO',
    landing: { 
      title: 'Rad Learning Tutor',
      subtitle: 'This activity will show you how our AI tutor works and help you understand our personalised learning approach.',
      videoId: 'i83lFA5vpho'
    },
    chat: { 
      exitAfter: 5,
      // Test config: include activity responses in LLM prompt
      includeActivityResponses: ['demo_pre_chat_question'],
      // Test config: auto-start with initial LLM message
      autoStartWithActivity: 'demo_pre_chat_question',
      // Test config: require exit feedback
      requireExitResponse: true,
    },
    flow: [
      { slug: 'landing', component: CourseLandingPage },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'activity', component: DemoActivityPage },
      { slug: 'chat', component: BaseChat },
    ],
  },
  // Example course with a different chat threshold but same base flow for now
  RMIT1: {
    code: 'RMIT1',
    landing: {
      copy: `# Rad Learning Tutor  

*Welcome RMIT students!*  

## ⚡ TL;DR – What you need to know  

- 🗣️ Chat with the AI tutor **about your presentation skills**  
- 🎯 It helps you **reflect and prepare**, but will not write your presentation  
- 🔒 Chats are **recorded**, but only used for learning. **Anonymous data** may be shared with RMIT  

## Presentation Skills Tutor  

### What is it?  
The **Presentation Skills Tutor** is an AI chat activity. It will **ask you questions** about your upcoming presentation and your skills. The goal is to **help you reflect** on what you already know and what you can improve before next week.  

### Why are we doing this?  
**Presentation skills are important** for your project, your study, and your future career. The tutor will **not create your presentation** for you. Instead, it will help you **think, practice, and build confidence** 💪.  

### How does it work?  
- 🗓️ You can chat with the tutor **this week only**  
- 💬 It will use your course materials to ask and respond to questions  
- ✅ This activity is **not assessed**  
- 🔒 Your chat is **not part of your university record**. It is only to **support your learning**  

## Consent to Having Your Conversation Recorded  

**Our tutor is a great listener! 🗣️✨**  

By chatting, you agree that:  
- **Rad Learning will record and review your conversation** with the Tutor  
- This is **only to support learning** and improve the Rad Learning experience  
- **Anonymous data** (no names attached) may be **shared with RMIT staff** to help improve teaching  
- Your chat is **not assessed** and will **not affect your grades**  `,
    },
    chat: { exitAfter: 5 },
    flow: [
      { slug: 'landing', component: RMIT1Landing },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'chat', component: BaseChat },
    ],
  },
  // ADHD Writing course with questionnaire-based flow
  ADHDWRITE1: {
    code: 'ADHDWRITE1',
    landing: {
      title: 'ADHD Writing Workshop',
      subtitle: 'Pre-workshop questionnaire',
    },
    activities: {
      requirements: [
        { activity_key: 'accessibility_pen_paper', required: true },
        { activity_key: 'accessibility_other_needs', required: false },
        { activity_key: 'writing_goals', required: true },
        { activity_key: 'strategies_getting_started', required: true },
        { activity_key: 'barriers_getting_started', required: true },
        { activity_key: 'barriers_staying_engaged', required: true },
      ],
      enableChat: false, // No chat - just show thank you page
    },
    flow: [
      { slug: 'landing', component: ADHDWRITE1Landing },
      { slug: 'thank-you', component: ADHDWRITE1ThankYou },
    ],
  },
  // Neurodiversity Learning Experience (migrated from V1)
  ND101: {
    code: 'ND101',
    landing: {
      title: 'Neurodiversity Learning Experience',
      subtitle: 'Welcome to the first part of the Neurodiversity Learning Experience!',
    },
    chat: { exitAfter: 5 },
    flow: [
      { slug: 'landing', component: ND101Landing },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'chat', component: BaseChat },
    ],
  },
  // ND101-ACON1 - Neurodiversity Learning Experience for ACON Cohort 1
  'ND101-ACON1': {
    code: 'ND101-ACON1',
    landing: {
      title: 'Neurodiversity Learning Experience',
      subtitle: 'Welcome to the first part of the Neurodiversity Learning Experience!',
    },
    chat: { exitAfter: 5 },
    flow: [
      { slug: 'landing', component: ND101ACON1Landing },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'chat', component: BaseChat },
    ],
  },
  // ND101-ACON1A - Neurodiversity Learning Experience for ACON Cohort 1 - Post Workshop
  'ND101-ACON1A': {
    code: 'ND101-ACON1A',
    landing: {
      title: 'Rad Learning Tutor',
      subtitle: 'Complete your Neurodiversity Learning Experience',
    },
    chat: {
      exitAfter: 5,
      includeActivityResponses: ['post_workshop_NLE'],
      autoStartWithActivity: 'post_workshop_NLE',
      requireExitResponse: true,
    },
    flow: [
      { slug: 'landing', component: ND101ACON1ALanding },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'activity', component: ND101ACON1AActivityPage },
      { slug: 'chat', component: BaseChat },
    ],
  },
  // RMIT2 - Presentation Skills 2026 Cohort (Multi-week course)
  RMIT2: {
    code: 'RMIT2',
    landing: {
      copy: `# RMIT Presentation Skills Tutor - Week 1

## Welcome to The Rad Learning Tutor! 

You've already reviewed the key concepts, so now it's time to have a conversation with the tutor. 

**The goal for week 1 is to decide how you need to develop your professional presentation skills.**

## Consent to Having Your Conversation Recorded

**Our tutor is a great listener! 🗣️✨**

By chatting, you agree that:
- **Rad Learning will record and review your conversation** with the Tutor
- This is **only to support learning** and improve the Rad Learning experience
- **Anonymous data** (no names attached) may be **shared with RMIT staff** to help improve teaching
- Your chat is **not assessed** and will **not affect your grades**`,
    },
    chat: { exitAfter: 5 },
    flow: [
      { slug: 'landing', component: RMIT1Landing },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'chat', component: BaseChat },
    ],
  },
  // RMIT2A - Presentation Skills 2026 Cohort (Week 2 with exit activity)
  RMIT2A: {
    code: 'RMIT2A',
    landing: {
      copy: `# RMIT Presentation Skills Tutor - Week 2

## This Week's Learning Goals 👩🏽‍💻
- Understand the 3-part structure for technical presentations
- Learn the demo-first approach for showcasing technical work
- Plan content structure and timing for your presentation
- Create an outline for your project presentation

## Consent to Having Your Conversation Recorded

**Our tutor is a great listener! 🗣️✨**

By chatting, you agree that:
- **Rad Learning will record and review your conversation** with the Tutor
- This is **only to support learning** and improve the Rad Learning experience
- **Anonymous data** (no names attached) may be **shared with RMIT staff** to help improve teaching
- Your chat is **not assessed** and will **not affect your grades**`,
    },
    chat: { 
      exitAfter: 5,
      requireExitResponse: true,
      successMessages: {
        title: "You're all done!",
        message: "Thanks for reflecting on your presentation outline."
      }
    },
    flow: [
      { slug: 'landing', component: RMIT1Landing },
      { slug: 'preferences', component: BasePreferences },
      { slug: 'chat', component: BaseChat },
    ],
  },
};

export type CourseRegistry = typeof courseRegistry;
